import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useAuthStore } from '../store/authStore';
import { colors } from '../utils/colors';
import { typography } from '../utils/typography';
import { spacing } from '../utils/theme';

export const AccountSettingsScreen: React.FC = () => {
  const navigation = useNavigation();
  const { profile, logout } = useAuthStore();

  const handleEditProfile = () => {
    Alert.alert('プロフィール編集', '今後実装予定');
  };

  const handleChangePassword = () => {
    Alert.alert('パスワード変更', '今後実装予定');
  };

  const handleExportData = () => {
    Alert.alert('データエクスポート', 'アカウントデータのエクスポート機能は今後実装予定です');
  };

  const handleDeleteAccount = () => {
    Alert.alert(
      'アカウント削除',
      'アカウントを削除しますか？この操作は取り消せません。',
      [
        { text: 'キャンセル', style: 'cancel' },
        {
          text: '削除',
          style: 'destructive',
          onPress: () => Alert.alert('エラー', 'アカウント削除機能は今後実装予定です'),
        },
      ]
    );
  };

  const handleLogout = () => {
    Alert.alert('ログアウト', 'ログアウトしますか？', [
      { text: 'キャンセル', style: 'cancel' },
      {
        text: 'ログアウト',
        style: 'destructive',
        onPress: async () => {
          try {
            await logout();
          } catch (error) {
            console.error('Logout error:', error);
            Alert.alert('エラー', 'ログアウトに失敗しました');
          }
        },
      },
    ]);
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>アカウント設定</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView>
        {/* アカウント情報セクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>アカウント情報</Text>

          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>表示名</Text>
            <Text style={styles.infoValue}>{profile?.displayName || 'ユーザー名'}</Text>
          </View>

          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>ハンドル</Text>
            <Text style={styles.infoValue}>@{profile?.handle || 'handle'}</Text>
          </View>

          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>DID</Text>
            <Text style={styles.infoValue} numberOfLines={1}>
              {profile?.did || ''}
            </Text>
          </View>
        </View>

        {/* プロフィール管理セクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>プロフィール管理</Text>

          <TouchableOpacity style={styles.option} onPress={handleEditProfile}>
            <View style={styles.optionLeft}>
              <Ionicons name="create-outline" size={22} color={colors.textSecondary} />
              <Text style={styles.optionLabel}>プロフィールを編集</Text>
            </View>
            <Ionicons name="chevron-forward" size={22} color={colors.textSecondary} />
          </TouchableOpacity>

          <TouchableOpacity style={styles.option} onPress={handleChangePassword}>
            <View style={styles.optionLeft}>
              <Ionicons name="key-outline" size={22} color={colors.textSecondary} />
              <Text style={styles.optionLabel}>パスワードを変更</Text>
            </View>
            <Ionicons name="chevron-forward" size={22} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* データ管理セクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>データ管理</Text>

          <TouchableOpacity style={styles.option} onPress={handleExportData}>
            <View style={styles.optionLeft}>
              <Ionicons name="download-outline" size={22} color={colors.textSecondary} />
              <Text style={styles.optionLabel}>データをエクスポート</Text>
            </View>
            <Ionicons name="chevron-forward" size={22} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* 危険な操作セクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>アカウント操作</Text>

          <TouchableOpacity style={styles.option} onPress={handleLogout}>
            <View style={styles.optionLeft}>
              <Ionicons name="log-out-outline" size={22} color={colors.error} />
              <Text style={[styles.optionLabel, styles.dangerText]}>ログアウト</Text>
            </View>
            <Ionicons name="chevron-forward" size={22} color={colors.error} />
          </TouchableOpacity>

          <TouchableOpacity style={styles.option} onPress={handleDeleteAccount}>
            <View style={styles.optionLeft}>
              <Ionicons name="trash-outline" size={22} color={colors.error} />
              <Text style={[styles.optionLabel, styles.dangerText]}>アカウントを削除</Text>
            </View>
            <Ionicons name="chevron-forward" size={22} color={colors.error} />
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  backButton: {
    padding: 4,
    width: 32,
  },
  headerTitle: {
    ...typography.h2,
    color: colors.text,
    flex: 1,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 32,
  },
  section: {
    marginTop: spacing.lg,
    paddingHorizontal: spacing.md,
  },
  sectionTitle: {
    ...typography.h4,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  infoItem: {
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  infoLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    marginBottom: 4,
  },
  infoValue: {
    ...typography.body,
    color: colors.text,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  optionLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  optionLabel: {
    ...typography.body,
    color: colors.text,
    marginLeft: spacing.md,
  },
  dangerText: {
    color: colors.error,
  },
});
